import { useState, useEffect } from 'react';
import AppShell from '../components/layout/AppShell';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { studentAPI, authAPI } from '../utils/api';
import StreakCalendar from '../components/study/StreakCalendar';

const SUBJECTS_BY_CLASS = {
  6: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  7: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  8: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  9: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi', 'AI / CT'],
  10: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi', 'AI / CT'],
};

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const { success, error } = useToast();
  const [activeTab, setActiveTab] = useState('profile');
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', date_of_birth: user?.date_of_birth || '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [showClassChange, setShowClassChange] = useState(false);
  const [classChangeReq, setClassChangeReq] = useState({ newClass: '', reason: '' });
  const [uploadingPic, setUploadingPic] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [stats, setStats] = useState(null);
  const [subjects, setSubjects] = useState(() => {
    try {
      const s = user?.subjects;
      if (!s) return [];
      if (Array.isArray(s)) return s;
      // Handle double-encoded JSON
      let parsed = JSON.parse(s);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });

  useEffect(() => {
    loadStats();

  }, []);

  const loadStats = async () => {
    try {
      const res = await studentAPI.getProgress();
      const data = res.data.data;
      const total = data.subjects?.reduce((a, s) => a + (s.totalTopics || 0), 0) || 0;
      const mastered = data.subjects?.reduce((a, s) => a + (s.masteredTopics || 0), 0) || 0;
      const avgMastery = data.subjects?.length > 0
        ? Math.round(data.subjects.reduce((a, s) => a + (s.mastery || 0), 0) / data.subjects.length)
        : 0;
      setStats({ total, mastered, avgMastery });
    } catch {}
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await studentAPI.updateProfile(form);
      updateUser(form);
      success('Profile updated!');
    } catch { error('Update failed.'); }
    finally { setLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword.length < 8) return error('Password must be at least 8 characters.');
    if (pwForm.newPassword !== pwForm.confirmPassword) return error('Passwords do not match.');
    setPwLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      success('Password changed successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch(err) { error(err.response?.data?.message || 'Failed to change password.'); }
    finally { setPwLoading(false); }
  };

  const handleClassChangeRequest = async (e) => {
    e.preventDefault();
    try {
      await studentAPI.requestClassChange({ newClassId: classChangeReq.newClass, reason: classChangeReq.reason });
      success('Class change request submitted! Admin will review.');
      setShowClassChange(false);
    } catch { error('Failed to submit request.'); }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('picture', file);
      const res = await studentAPI.uploadProfilePicture(formData);
      setAvatarUrl(res.data.data.avatar_url + '?t=' + Date.now());
      success('Profile picture updated!');
    } catch { error('Upload failed. Max size 2MB, JPG/PNG only.'); }
    finally { setUploadingPic(false); }
  };

  const toggleSubject = async (subj) => {
    const newSubjects = subjects.includes(subj)
      ? subjects.filter(s => s !== subj)
      : [...subjects, subj];
    setSubjects(newSubjects);
    try {
      await studentAPI.updateProfile({ subjects: newSubjects });
      updateUser({ subjects: newSubjects });
      success(subjects.includes(subj) ? subj + ' removed' : subj + ' added');
    } catch { error('Failed to update subjects.'); }
  };

  const classId = user?.classId || user?.class_id;
  const availableSubjects = SUBJECTS_BY_CLASS[classId] || SUBJECTS_BY_CLASS[10];
  const tabs = ['profile', 'security', 'subjects', 'activity'];

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1.5px solid var(--border)', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    background: 'white'
  };
  const labelStyle = {
    fontSize: '11px', fontWeight: 700, color: 'var(--muted)',
    display: 'block', marginBottom: '6px',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  };

  return (
    <AppShell>
      <div className="topbar">
        <div style={{ fontSize: '15px', fontWeight: 800 }}>My Profile</div>
      </div>

      <div className="page-content" style={{ maxWidth: '680px' }}>

        {/* Profile Header Card */}
        <div className="card fade-up-1" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>

            {/* Avatar with upload */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile"
                  style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--brand-primary)' }} />
              ) : (
                <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '24px', color: 'white' }}>
                  {user?.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
              )}
              <label style={{ position: 'absolute', bottom: '0', right: '0', width: '22px', height: '22px', borderRadius: '50%', background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px', color: 'white', border: '2px solid white' }}>
                {uploadingPic ? '⏳' : '✎'}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePictureUpload} style={{ display: 'none' }} />
              </label>
            </div>

            {/* Name + username + class */}
            <div style={{ flex: 1, minWidth: '150px' }}>
              <div style={{ fontSize: '20px', fontWeight: 900, color: 'var(--text)' }}>{user?.name}</div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                Class {user?.classId || user?.class_id} · CBSE
                {user?.date_of_birth && ` · 🎂 ${new Date(user.date_of_birth).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}`}
              </div>
              {user?.username && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--brand-primary-light)', borderRadius: '20px', padding: '3px 10px', marginTop: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--brand-primary)' }}>@{user.username}</span>
                </div>
              )}
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {[
                { val: stats?.mastered || 0, lbl: 'Mastered', color: 'var(--green)' },
                { val: stats?.total || 0, lbl: 'Topics', color: 'var(--brand-secondary)' },
                { val: (stats?.avgMastery || 0) + '%', lbl: 'Avg Score', color: 'var(--brand-primary)' },
              ].map(s => (
                <div key={s.lbl} style={{ background: 'var(--bg)', borderRadius: '10px', padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 900, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600, marginTop: '2px' }}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'var(--bg)', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize', background: activeTab === t ? 'white' : 'transparent', color: activeTab === t ? 'var(--brand-primary)' : 'var(--muted)', boxShadow: activeTab === t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
              {t === 'profile' ? '👤 Profile' : t === 'security' ? '🔒 Security' : t === 'subjects' ? '📚 Subjects' : '📅 Activity'}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Edit info */}
            <div className="card fade-up-2">
              <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '16px' }}>Personal Information</div>
              <form onSubmit={handleUpdateProfile}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone Number</label>
                    <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="10-digit mobile" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Date of Birth 🎂</label>
                    <input type="date" value={form.date_of_birth} onChange={e => setForm(p => ({ ...p, date_of_birth: e.target.value }))} style={inputStyle} />
                    <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '4px' }}>We'll wish you on your birthday! 🎉</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Email (cannot change)</label>
                    <input value={user?.email || ''} disabled style={{ ...inputStyle, opacity: 0.5, cursor: 'not-allowed' }} />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  style={{ background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>

            {/* Account details */}
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>Account Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[
                  { lbl: 'Username', val: '@' + (user?.username || '—') },
                  { lbl: 'Email', val: user?.email || '—' },
                  { lbl: 'Class', val: 'Class ' + (user?.classId || user?.class_id || '—') + ' — CBSE' },
                  { lbl: 'Account Type', val: user?.registration_path === 'school' ? '🏫 School Account' : '👤 Individual' },
                  { lbl: 'Status', val: user?.status === 'active' ? '✅ Active' : user?.status },
                  { lbl: 'Subjects Enrolled', val: (Array.isArray(subjects) ? subjects.length : 0) + ' subjects' },
                ].map(f => (
                  <div key={f.lbl} style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '3px' }}>{f.lbl}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700 }}>{f.val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Class change */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 800 }}>Request Class Change</div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px', maxWidth: '340px' }}>
                    Submit a request to move to a different class. Admin will approve. Your current progress will be archived safely.
                  </div>
                </div>
                <button onClick={() => setShowClassChange(!showClassChange)}
                  style={{ background: 'var(--brand-secondary-light)', color: 'var(--brand-secondary)', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                  {showClassChange ? 'Cancel' : 'Request Change'}
                </button>
              </div>
              {showClassChange && (
                <form onSubmit={handleClassChangeRequest} style={{ marginTop: '14px', padding: '14px', background: '#FFFBEB', borderRadius: '8px', border: '1px solid #FCD34D' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400E', marginBottom: '12px' }}>⚠️ Your current progress will be archived — not deleted</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={labelStyle}>New Class *</label>
                      <select value={classChangeReq.newClass} onChange={e => setClassChangeReq(p => ({ ...p, newClass: e.target.value }))} required style={inputStyle}>
                        <option value="">Select class</option>
                        {[6,7,8,9,10].filter(c => c !== user?.class_id).map(c => <option key={c} value={c}>Class {c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Reason</label>
                      <input value={classChangeReq.reason} onChange={e => setClassChangeReq(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Promoted to next class" style={inputStyle} />
                    </div>
                  </div>
                  <button type="submit"
                    style={{ background: 'var(--brand-secondary)', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
                    Submit Request
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="card fade-up-2">
            <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '16px' }}>Change Password</div>
            <form onSubmit={handleChangePassword}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>Current Password *</label>
                  <input type="password" value={pwForm.currentPassword} onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))} placeholder="Your current password" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>New Password *</label>
                  <input type="password" value={pwForm.newPassword} onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))} placeholder="Minimum 8 characters" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Confirm New Password *</label>
                  <input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))} placeholder="Repeat new password" required style={inputStyle} />
                </div>
              </div>
              <button type="submit" disabled={pwLoading}
                style={{ background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', opacity: pwLoading ? 0.7 : 1 }}>
                {pwLoading ? 'Changing...' : '🔒 Change Password'}
              </button>
            </form>
          </div>
        )}

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <div className="card fade-up-2">
            <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>My Subjects</div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '16px' }}>
              Tap to add or remove subjects. Changes take effect immediately.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {availableSubjects.map(s => {
                const enrolled = subjects.includes(s);
                return (
                  <div key={s} onClick={() => toggleSubject(s)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '10px', border: enrolled ? '2px solid var(--brand-secondary)' : '1.5px solid var(--border)', background: enrolled ? 'var(--brand-secondary-light)' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: enrolled ? 'var(--brand-secondary)' : 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: 'white', flexShrink: 0, fontWeight: 800 }}>
                      {enrolled ? '✓' : '+'}
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: enrolled ? 'var(--brand-secondary)' : 'var(--muted)' }}>{s}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--muted)', background: 'var(--bg)', padding: '10px 12px', borderRadius: '8px' }}>
              📌 {subjects.length} subjects enrolled · Available subjects depend on your class
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <StreakCalendar />
          </div>
        )}

      </div>
    </AppShell>
  );
};

export default ProfilePage;
